import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Dumbbell, 
  Clock, 
  Target, 
  Play, 
  CheckCircle,
  RotateCcw,
  Calendar,
  TrendingUp,
  Loader2
} from "lucide-react";
import Navigation from "./Navigation";
import { clientApi, Client, workoutPlanApi, WorkoutPlan, exerciseApi, Exercise } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const GymPlan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, clientName, isLoading } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<WorkoutPlan | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [completedExercises, setCompletedExercises] = useState<string[]>([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);

  // Get client name from authentication context
  const getClientName = () => {
    return clientName; // Use the authenticated client name from AuthContext
  };

  // Redirect to auth if not authenticated (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch client data and their workout plans
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    const clientName = getClientName();
    if (!clientName) {
      setError('No client information found');
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Set today as the initially selected day
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const weekDays = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const todayIndex = weekDays.indexOf(today);
    setSelectedDayIndex(todayIndex !== -1 ? todayIndex : 0);
    
    // Fetch client data
    clientApi.getAll()
      .then(clients => {
        const foundClient = clients.find(c => 
          c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
        );
        
        if (foundClient) {
          setClient(foundClient);
          
          // Fetch workout plans for this client
          return Promise.all([
            workoutPlanApi.getAll(foundClient._id),
            exerciseApi.getAll()
          ]);
        } else {
          setError('Client not found');
          return Promise.resolve([[], []]);
        }
      })
      .then(([plans, exerciseList]) => {
        setWorkoutPlans(plans);
        setExercises(exerciseList);
        
        // Select the most recent active plan
        if (plans.length > 0) {
          const activePlans = plans.filter(p => p.status === 'active');
          if (activePlans.length > 0) {
            const sortedPlans = activePlans.sort((a, b) => 
              new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
            );
            setSelectedPlan(sortedPlans[0]);
          } else {
            setSelectedPlan(plans[0]); // Fallback to first plan if no active ones
          }
        }
      })
      .catch(err => {
        console.error('Error fetching client or workout plans:', err);
        setError('Failed to load client data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, clientName]);

  // Get selected day's workout session
  const getSelectedWorkout = useMemo(() => {
    if (!selectedPlan?.workoutSessions) return null;
    
    // If no day is selected, default to today
    if (selectedDayIndex === null) {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const weekDays = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const todayIndex = weekDays.indexOf(today);
      
      if (todayIndex === -1) return null;
      
      const todaySession = selectedPlan.workoutSessions[todayIndex];
      if (todaySession && todaySession.exercises && todaySession.exercises.length > 0) {
        return todaySession;
      }
      return null;
    }
    
    // Get the session for the selected day
    const selectedSession = selectedPlan.workoutSessions[selectedDayIndex];
    
    if (!selectedSession) {
      return null; // No session for selected day
    }
    
    // Only return session if it has exercises
    if (selectedSession.exercises && selectedSession.exercises.length > 0) {
      return selectedSession;
    }
    
    return null; // Session exists but has no exercises (rest day)
  }, [selectedPlan, selectedDayIndex]);

  // Generate weekly schedule from workout sessions
  const weeklySchedule = useMemo(() => {
    if (!selectedPlan?.workoutSessions) {
      return [
        { day: "Saturday", focus: "Rest Day", status: "rest" },
    { day: "Sunday", focus: "Rest Day", status: "rest" },
        { day: "Monday", focus: "Rest Day", status: "rest" },
        { day: "Tuesday", focus: "Rest Day", status: "rest" },
        { day: "Wednesday", focus: "Rest Day", status: "rest" },
        { day: "Thursday", focus: "Rest Day", status: "rest" },
        { day: "Friday", focus: "Rest Day", status: "rest" },
      ];
    }

    // Define the 7 days starting from Saturday
    const weekDays = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    
    return weekDays.map((day, index) => {
      // Find the session for this day (should be the session at this index)
      const session = selectedPlan.workoutSessions[index];
      let status = 'rest';
      let focus = 'Rest Day';
      
      if (session) {
        // Always show the session name, even if it's empty
        focus = session.name;
        
        // Check if this session has exercises
        if (session.exercises && session.exercises.length > 0) {
          if (day === today) status = 'active';
          else if (weekDays.indexOf(day) < weekDays.indexOf(today)) status = 'completed';
          else status = 'upcoming';
        }
        // If no exercises, it remains 'rest' status
      }
      
      return {
        day: day.charAt(0).toUpperCase() + day.slice(1),
        focus: focus,
        status
      };
    });
  }, [selectedPlan]);

  const toggleExerciseComplete = (exerciseId: string) => {
    setCompletedExercises(prev => 
      prev.includes(exerciseId) 
        ? prev.filter(id => id !== exerciseId)
        : [...prev, exerciseId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-success text-success-foreground";
      case "active": return "bg-primary text-primary-foreground";
      case "upcoming": return "bg-secondary text-secondary-foreground";
      case "rest": return "bg-muted text-muted-foreground";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  const workoutProgress = getSelectedWorkout 
    ? (completedExercises.length / getSelectedWorkout.exercises.length) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Gym Training Plan</h1>
          <p className="text-muted-foreground">
            {loading ? 'Loading your personalized workout program...' : 
             error ? 'Error loading workout data' :
             client ? `Personalized workout program for ${client.name}` :
             'Your personalized workout program'}
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-muted-foreground">Loading your workout data...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-2 text-destructive">
              <span className="text-sm font-medium">{error}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              No workout plans found. Please contact your trainer to set up your personalized workout program.
            </p>
          </div>
        )}

        {!loading && !error && (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Weekly Schedule */}
          <div className="xl:col-span-1">
              <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span>This Week</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {weeklySchedule.map((day, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between p-3 rounded-lg border border-border cursor-pointer transition-colors hover:border-primary/30 ${
                          selectedDayIndex === index ? 'bg-primary/10 border-primary' : ''
                        }`}
                        onClick={() => setSelectedDayIndex(index)}
                      >
                      <div>
                        <p className="font-medium text-sm">{day.day}</p>
                        <p className="text-xs text-muted-foreground">{day.focus}</p>
                      </div>
                      <Badge className={getStatusColor(day.status)} variant="secondary">
                        {day.status === "active" ? "Today" : day.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Today's Workout */}
          <div className="xl:col-span-3">
              <Card className="h-fit">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <Dumbbell className="w-5 h-5 text-primary" />
                      <span>
                        {getSelectedWorkout ? `${weeklySchedule[selectedDayIndex || 0]?.day}'s Workout - ${getSelectedWorkout.name}` : 'No Workout Scheduled'}
                      </span>
                  </CardTitle>
                    {getSelectedWorkout && (
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Progress</p>
                          <p className="font-bold text-primary">{completedExercises.length}/{getSelectedWorkout.exercises.length}</p>
                    </div>
                    <div className="w-20">
                      <Progress value={workoutProgress} className="h-2" />
                    </div>
                  </div>
                    )}
                </div>
              </CardHeader>
              <CardContent>
                  {!getSelectedWorkout ? (
                    <div className="text-center py-8">
                      <Dumbbell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-muted-foreground mb-2">
                        No Workout Scheduled
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {workoutPlans.length === 0 
                          ? "No workout plans have been created for you yet. Please contact your trainer to set up your personalized workout program."
                          : "No workout is scheduled for this day. Check your weekly schedule for upcoming workouts."
                        }
                      </p>
                    </div>
                  ) : (
                <div className="space-y-6">
                      {getSelectedWorkout.exercises.map((exerciseEntry, index) => {
                        const exercise = exercises.find(e => e._id === exerciseEntry.exercise);
                        const exerciseId = `exercise-${index}`;
                        const isCompleted = completedExercises.includes(exerciseId);
                        
                        if (!exercise) return null;
                        
                    return (
                      <div 
                            key={exerciseId} 
                            className={`p-6 rounded-lg border transition-colors ${
                          isCompleted 
                            ? "bg-success/10 border-success/20" 
                            : "bg-card border-border hover:border-primary/30"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold mb-2 ${isCompleted ? "text-success line-through" : "text-foreground"}`}>
                              {exercise.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3">{exercise.instructions}</p>
                            
                                <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="flex items-center space-x-2">
                                <Target className="w-4 h-4 text-primary" />
                                    <span>{exerciseEntry.sets} sets</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RotateCcw className="w-4 h-4 text-primary" />
                                    <span>{exerciseEntry.reps} reps</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col space-y-2 ml-4">
                            <Button
                              variant={isCompleted ? "default" : "outline"}
                              size="sm"
                                  onClick={() => toggleExerciseComplete(exerciseId)}
                              className={`min-w-[100px]${isCompleted ? " bg-success text-success-foreground" : ""}`}
                            >
                              {isCompleted ? (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Complete
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  Start
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                  )}

                {/* Workout Summary */}
                  {getSelectedWorkout && workoutProgress === 100 && (
                  <div className="mt-8 p-6 rounded-lg bg-success/10 border border-success/20 text-center">
                    <div className="mb-4">
                      <CheckCircle className="w-12 h-12 text-success mx-auto mb-2" />
                      <h3 className="text-lg font-semibold text-success">Workout Complete! 🎉</h3>
                        <p className="text-sm text-muted-foreground">Great job finishing today's workout</p>
                    </div>
                    <div className="flex justify-center space-x-4">
                      <Button variant="default" className="bg-success text-success-foreground">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        View Progress
                      </Button>
                      <Button variant="outline">
                        Save Workout
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default GymPlan;